import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import ShipmentUpdateFlag from '../../../../General/ShipmentUpdateFlag';
import { fetchZonosData, configureStore, resetConfigureState } from './zonosSlice';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const Item = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    height: '100%'
}));

const ZonosIntegration = ({ storeName, storeId }) => {
    const dispatch = useDispatch();

    const profile = useSelector(state => state.auth);
    const progress = useSelector(state => state.zonos && state.zonos.progress);
    const configure = useSelector(state => state.zonos && state.zonos.configure);
    const [alert, setAlert] = useState(false);
    const [snackOpen, setSnackOpen] = useState(false);
    const [values, setValues] = useState(progress.data);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (progress && progress.data && progress.data.zonos_key) {
            setValues({
                ...values,
                ...progress.data
            })
        }
    }, [progress && progress.data])

    useEffect(() => {
        if (configure && configure.message) {
            setAlert(true)
            setSnackOpen(true)
        } else if (configure && configure.hasErrors == true) {
            setAlert(true)
            setSnackOpen(true)
        }
    }, [dispatch, configure && configure.message])

    useEffect(() => {
        if (storeName) {
            let apikey = profile.profile.api_token
            const data = { apikey: apikey, store_name: storeName, store_id: storeId }
            dispatch(fetchZonosData(data));
        }
    }, [dispatch]);

    const validator = (fieldValues) => {
        let temp = { ...errors }
        if ('zonos_key' in fieldValues)
            temp.zonos_key = fieldValues.zonos_key ? "" : "This field is required."

        setErrors({
            ...temp
        })

        if (fieldValues === values)
            return Object.values(temp).every(x => x == "")
    }

    const onInputChange = e => {
        const { name, value } = e.target
        setValues({
            ...values,
            [name]: value
        })
        validator({ [name]: value })
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        const isValid = validator(values)
        if (isValid) {
            dispatch(configureStore(values));
        }
    }

    const handleClose = () => {
        dispatch(resetConfigureState())
        setSnackOpen(false);
    };
    return (
        <>
            {alert &&
                <Snackbar
                    open={snackOpen}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    autoHideDuration={6000}
                    onClose={handleClose}
                >
                    <Alert
                        variant="filled"
                        severity=
                        {configure && configure.hasErrors === true ? "error" : "success"}
                        sx={{ 
                            justifyContent: 'center', 
                            margin: "10px 0",
                            borderRadius: '12px',
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <span > {configure && configure.message}</span>
                    </Alert>
                </Snackbar>
            }
            {progress && progress.loading === false &&
                <Grid container spacing={0} justifyContent="center" sx={{ my: 4 }}>
                    <Grid item xs={12} sm={10} md={7} lg={6}>
                        <Card
                            sx={{
                                p: { xs: 3, md: 5 },
                                borderRadius: '16px',
                                border: '1px solid #E2E8F0',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
                                backgroundColor: '#FFFFFF',
                                transition: 'transform 0.2s ease-in-out'
                            }}
                        >
                            <Item>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 3.5 }}>
                                    
                                    {/* App Brand Branding Element Section */}
                                    <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderRadius: '14px', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Box
                                            component="img"
                                            src="/assets/images/integration/ZONOS.png"
                                            alt="ZONOS"
                                            sx={{ width: 'auto', height: '40px', objectFit: 'contain' }}
                                        />
                                    </Box>

                                    {/* Context Header Headline Info text */}
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '18px', mb: 0.5, letterSpacing: '-0.3px' }}>
                                            Connect Zonos API
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748B', maxWidth: '340px', mx: 'auto', lineHeight: 1.5 }}>
                                            Provide your Zonos production credential token to activate automated cross-border duties and custom taxation syncing.
                                        </Typography>
                                    </Box>

                                    {/* API Credential Outlined Text Input Container Element */}
                                    <Box className="sc--form--center" sx={{ width: '100%', maxWidth: '420px' }}>
                                        <TextField
                                            label="API Key"
                                            name="zonos_key"
                                            size="small"
                                            value={values.zonos_key}
                                            onChange={onInputChange}
                                            error={!!errors.zonos_key}
                                            helperText={errors.zonos_key}
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    '& fieldset': { borderColor: '#E2E8F0' },
                                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                    '&.Mui-focused fieldset': { borderColor: '#1976d2' }
                                                },
                                                '& .MuiInputLabel-root': { color: '#64748B', fontWeight: 500 },
                                                '& .MuiFormHelperText-root': { mx: 0.5, mt: 0.75, fontWeight: 500 }
                                            }}
                                        />
                                    </Box>

                                    {/* Form Submit Execution Control Area */}
                                    <Box className="sc--form--center" sx={{ width: '100%', display: 'flex', justifyContent: 'center', borderTop: '1px solid #F1F5F9', pt: 3 }}>
                                        <LoadingButton
                                            variant="contained"
                                            onClick={e => handleSubmit(e)}
                                            loading={configure && configure.loading}
                                            endIcon={<SendOutlinedIcon sx={{ fontSize: '15px !important', ml: 0.5 }} />}
                                            sx={{
                                                px: 4,
                                                py: 1.25,
                                                borderRadius: '8px',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                fontSize: '13.5px',
                                                boxShadow: 'none',
                                                backgroundColor: '#1976d2',
                                                '&:hover': { backgroundColor: '#115293', boxShadow: 'none' },
                                                '& .MuiLoadingButton-loadingIndicator': { color: '#94A3B8' }
                                            }}
                                        >
                                            {(storeName) ? "Verify & Save" : "Verify & Save"}
                                        </LoadingButton>
                                    </Box>
                                    
                                </Box>
                            </Item>
                        </Card>
                    </Grid>
                </Grid>
            }
        </>
    )
}

export default ZonosIntegration;